<template>
  <div
    ref="rootRef"
    class="hotelux-location"
    :class="{ 'is-jp': isJapan }"
    v-click-outside="closePanel"
  >
    <el-autocomplete
      ref="autocompleteRef"
      v-model="locationLabel"
      :disabled="locationDisabled"
      :fetch-suggestions="querySearch"
      clearable
      hide-loading
      highlight-first-item
      class="location-input"
      popper-class="hotelux-location-popper"
      :append-to="appendTo"
      valueKey="showLabel"
      :placeholder="placeholder || t('shared.hotel.search.locationPlaceholder')"
      @select="handleSelect"
      @blur="handleBlur"
      @focus="handleFocus"
      @input="handleInput"
    >
      <template #prefix>
        <font-icon
          class="location-icon icon-hotelux"
          icon="icon-hotelux-Locate"
        />
      </template>
      <template #default="{ item }">
        <suggestion-item :item="item" />
      </template>
    </el-autocomplete>

    <div v-if="isPanelOpen" class="location-panel">
      <div
        v-if="historyList.length > 0 && userStore.user"
        class="panel-section is-history"
      >
        <div class="section-header">
          <UIText type="bold" class="section-title">
            {{ t("shared.common.historySearch") }}
          </UIText>
          <font-icon
            class="delete-icon icon-hotelux"
            icon="icon-hotelux-Delete"
            @click.stop="handleDeleteHistory"
          />
        </div>
        <div
          class="chip-row is-single-line"
          @wheel="handleChipRowWheel"
          @pointerdown="handleChipRowPointerDown"
        >
          <button
            v-for="item in historyList"
            :key="item._id || item.text"
            type="button"
            class="chip"
            @click="handleSelectHistory(item)"
          >
            <UIText>{{ item.text }}</UIText>
          </button>
        </div>
      </div>

      <div v-if="popularList.length > 0" class="panel-section">
        <div class="section-header">
          <UIText type="bold" class="section-title">
            {{ t("shared.common.hotDestinationsTitle") }}
          </UIText>
        </div>
        <div class="chip-row">
          <button
            v-for="item in popularList"
            :key="item._id || item.name"
            type="button"
            class="chip"
            @click="handleSelectCity(item)"
          >
            <UIText>{{ item.name }}</UIText>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 吸顶开关会在 hero 与 header 之间重建整条搜索栏，输入草稿放在模块作用域才能跨实例保留
const locationLabelDraft = ref(null);
</script>

<script setup>
import common from "@charmdeer/common";
import { resolveLocationSelection } from "@packages/booking/hotel/Filter/locationSelect";
import { ClickOutside as vClickOutside, ElMessageBox } from "element-plus";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
  watch
} from "vue";
import { useI18n } from "vue-i18n";

import { searchUniversalViaPost } from "@/api/biz/common";
import { getHoteluxCityList } from "@/api/rest/common";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useUserStoreHook } from "@/store/modules/user";

import UIText from "../UiText/index.vue";
import SuggestionItem from "./SuggestionItem.vue";

const props = defineProps({
  modelValue: { type: Object, default: () => null },
  locationDisabled: { type: Boolean, default: false },
  placeholder: { type: String, default: "" }
});

const emit = defineEmits(["update:modelValue"]);

const { t, locale } = useI18n();
const userStore = useUserStoreHook();
const hotelStore = useHotelStoreHook();
const { historyList } = storeToRefs(hotelStore);

const locationLabel = computed({
  get: () => locationLabelDraft.value ?? props.modelValue?.showLabel ?? "",
  set: value => {
    locationLabelDraft.value = value;
  }
});
const isPanelOpen = ref(false);
const autocompleteRef = ref(null);
const rootRef = ref(null);
const hotCityList = ref([]);
// 联想下拉默认挂到 body，空间不足时 popper 会把它翻到搜索栏上方；
// 挂到搜索栏容器上后即可用固定定位把它钉在搜索栏下方
const appendTo = ref("body");

const showHotelEnName = computed(() => userStore.language !== "en");
const isJapan = computed(() => userStore.language === "jp");

const popularList = computed(() =>
  hotCityList.value
    .map(mapCityByLang)
    .filter(item => item.country !== "中国台湾")
);

function mapCityByLang(city) {
  const lang = locale.value;
  if (lang === "zh") {
    return {
      ...city,
      name: city.city,
      countryName: city.country,
      provinceName: city.province
    };
  }

  const translation = city.translation?.[lang] || city.translation?.en;
  return {
    ...city,
    name: translation?.name || city.city,
    countryName: translation?.country || city.country,
    provinceName: translation?.province || city.province
  };
}

const resolveOptions = () => ({
  language: userStore.language,
  showHotelEnName: showHotelEnName.value
});

const emitLocation = location => {
  if (!location) return;
  emit("update:modelValue", location);
  closePanel();
  nextTick(() => {
    autocompleteRef.value?.blur();
  });
};

const openPanel = () => {
  isPanelOpen.value = true;
};

function closePanel() {
  isPanelOpen.value = false;
}

const handleSelectHistory = async item => {
  emitLocation(
    await resolveLocationSelection(
      { type: "history", data: item },
      resolveOptions()
    )
  );
};

const handleSelectCity = async city => {
  emitLocation(
    await resolveLocationSelection(
      { type: "city", data: city },
      resolveOptions()
    )
  );
};

const handleDeleteHistory = () => {
  ElMessageBox.confirm(t("shared.common.deleteHistory")).then(() => {
    hotelStore.deleteHistory();
  });
};

// 历史记录单行溢出，但滚动条按设计稿做了隐藏，
// 鼠标滚轮和按住拖动都不会触发原生横向滚动，这里手动把位移映射到 scrollLeft
const handleChipRowWheel = event => {
  const row = event.currentTarget;
  const delta =
    Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;
  const nextScrollLeft = row.scrollLeft + delta;

  if (
    nextScrollLeft <= 0 ||
    nextScrollLeft >= row.scrollWidth - row.clientWidth
  ) {
    return;
  }

  row.scrollLeft = nextScrollLeft;
  event.preventDefault();
};

const handleChipRowPointerDown = event => {
  if (event.button !== 0) return;

  const row = event.currentTarget;
  if (row.scrollWidth <= row.clientWidth) return;

  const startX = event.clientX;
  const startScrollLeft = row.scrollLeft;
  let isDragging = false;

  const handlePointerMove = moveEvent => {
    const distance = moveEvent.clientX - startX;
    if (!isDragging && Math.abs(distance) < 4) return;
    isDragging = true;
    row.scrollLeft = startScrollLeft - distance;
  };

  const handlePointerUp = () => {
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
    if (!isDragging) return;

    // 拖动结束时紧跟的 click 会命中 chip，需要拦掉以免误选
    const stopClick = clickEvent => clickEvent.stopPropagation();
    row.addEventListener("click", stopClick, { capture: true, once: true });
    setTimeout(() => {
      row.removeEventListener("click", stopClick, { capture: true });
    }, 300);
  };

  window.addEventListener("pointermove", handlePointerMove);
  window.addEventListener("pointerup", handlePointerUp);
  window.addEventListener("pointercancel", handlePointerUp);
};

const handleSelect = item => {
  emit("update:modelValue", item);
};

const handleBlur = () => {
  if (
    locationLabel.value &&
    locationLabel.value !== props.modelValue?.showLabel
  ) {
    emit("update:modelValue", {
      locationType: "search",
      showLabel: locationLabel.value,
      _id: locationLabel.value
    });
  } else if (!locationLabel.value && props.modelValue !== null) {
    emit("update:modelValue", null);
  }
};

const handleFocus = () => {
  if (!locationLabel.value) {
    openPanel();
  }
};

const handleInput = value => {
  if (value) {
    closePanel();
  } else {
    openPanel();
  }
};

const querySearch = async (queryString, cb) => {
  if (!queryString) {
    cb([]);
    return;
  }
  const result = [];
  const res = await searchUniversalViaPost(queryString);
  const { city = [], moduleTpHotel = [], googleMapPolitical = [] } = res;

  const searchItem = {
    locationType: "search",
    showLabel: queryString,
    _id: queryString
  };
  result.push(searchItem);

  city.forEach(item => {
    item.locationType = "city";
    item.showLabel = common.city.toString.name(item, userStore.language);
    item.nameEn = common.city.toString.country(item, userStore.language);
    item.showHotelEnName = true;
  });
  result.push(...city);

  const googleMapPoliticalResult = [];
  if (googleMapPolitical.length) {
    googleMapPolitical.forEach(item => {
      const hasCity = city.some(
        cityItem => cityItem.showLabel === item.description
      );
      if (!hasCity) {
        item.locationType = "googleMapPolitical";
        item.showLabel = item.description;
        item._id = item.place_id;
        googleMapPoliticalResult.push(item);
      }
    });
    result.push(...googleMapPoliticalResult);
  }

  moduleTpHotel.forEach(item => {
    item.locationType = "moduleTpHotel";
    item.showLabel = item.name;
    item.showHotelEnName = showHotelEnName.value;
  });
  result.push(...moduleTpHotel);

  cb(result);
};

watch(
  () => props.modelValue,
  newLocation => {
    // 选中或清空地点后草稿跟随模型回显
    locationLabelDraft.value = newLocation?.showLabel || "";
  },
  { deep: true }
);

onMounted(async () => {
  appendTo.value = rootRef.value?.closest(".search-bar-container") || "body";

  try {
    const [, cityData] = await Promise.all([
      hotelStore.getSearchHistory(),
      getHoteluxCityList()
    ]);
    hotCityList.value = cityData?.hotCities || [];
  } catch (error) {
    console.error("Failed to load discovery data:", error);
  }
});

onActivated(() => {
  hotelStore.getSearchHistory();
});

onDeactivated(() => {
  closePanel();
});

onBeforeUnmount(() => {
  closePanel();
});

defineOptions({
  name: "HoteluxLocation"
});
</script>

<style lang="scss" scoped>
.hotelux-location {
  .location-input {
    width: 100%;
  }

  // el-autocomplete 根元素由父组件传入 class，拿不到本组件的 scoped id，
  // 因此 :deep() 必须以 .hotelux-location 作为锚点
  :deep(.el-input__wrapper) {
    display: flex;
    align-items: center;
    padding: 0;
    background: transparent !important;
    box-shadow: none;
  }

  :deep(.el-input__prefix) {
    display: inline-flex;
    align-items: center;
    width: 20px;
    height: 20px;
    margin: 0 12px 0 2px;
  }

  :deep(.el-input__inner) {
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent !important;
    font-family: "lato-bold";
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #212223;

    &::placeholder {
      font-family: "lato-bold";
      font-size: 16px;
      color: #212223;
      opacity: 1;
    }
  }

  // 日文下输入内容用 Noto Sans JP Medium，占位文案用 Noto Sans JP 常规
  &.is-jp {
    :deep(.el-input__inner) {
      font-family: NotoSansJP-Variable, sans-serif;
      font-weight: 500;

      &::placeholder {
        font-family: NotoSansJP-Variable, sans-serif;
        font-weight: 400;
      }
    }
  }

  .location-icon {
    display: block;
    font-size: 20px;
    line-height: 1;
    color: #212223;
  }

  // 弹层挂在搜索栏容器上，与搜索栏同宽
  .location-panel {
    position: absolute;
    // 17px = 16px 间距 + 搜索栏 1px 下描边
    top: calc(100% + 17px);
    right: 0;
    left: 0;
    box-shadow: 0 10px 28px rgb(0 0 0 / 11%);
  }

  .panel-section {
    padding: 32px 40px;
    background: #fff;

    &.is-history {
      background: #eee;
    }
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 18px;
    margin-bottom: 20px;
  }

  .section-title {
    // font-family: "lato-bold";
    font-size: 10px;
    line-height: 18px;
    color: #757575;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  .delete-icon {
    font-size: 16px;
    color: #2a2c35;
    cursor: pointer;
  }

  .chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    // 历史记录单行展示，超出部分横向滚动
    &.is-single-line {
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      user-select: none; // 按住拖动滚动时避免选中文字

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .chip {
    display: inline-flex;
    align-items: center;
    height: 34px;
    padding: 0 12px;
    border: 0;
    border-radius: 0;
    background: #f8f8f8;
    // font-family: lato;
    font-size: 16px;
    color: #212223;
    white-space: nowrap;
    cursor: pointer;

    &.is-primary {
      font-family: "lato-bold";
    }
  }

  .is-history .chip {
    background: #fff;

    &.is-primary {
      background: #fff;
    }
  }
}
</style>
